import React from 'react';
import { useTranslation } from 'react-i18next';
import { BatteryCharging, MapPin, CheckCircle } from 'lucide-react';
import { ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

function AssetCockpit() {
  const { t } = useTranslation();

  // Mock data - w prawdziwej aplikacji to by przyszło z API
  const assetInfo = {
    name: 'Gdańsk Storage One',
    location: 'Gdańsk-North 110kV',
    status: 'Operational',
    soc: 45,
    power: 5.2,
    powerStatus: 'Discharging',
    cyclesYtd: 142
  };

  const dispatchData = [
    { time: '00:00', price: 300, soc: 20, dispatch: 0 },
    { time: '01:00', price: 280, soc: 20, dispatch: 0 },
    { time: '02:00', price: 260, soc: 25, dispatch: 0 },
    { time: '03:00', price: 250, soc: 35, dispatch: -20 },
    { time: '04:00', price: 240, soc: 50, dispatch: -25 },
    { time: '05:00', price: 280, soc: 70, dispatch: -30 },
    { time: '06:00', price: 400, soc: 95, dispatch: 0 },
    { time: '07:00', price: 550, soc: 95, dispatch: 0 },
    { time: '08:00', price: 650, soc: 95, dispatch: 0 },
    { time: '09:00', price: 580, soc: 70, dispatch: 0 },
    { time: '10:00', price: 480, soc: 50, dispatch: 0 },
    { time: '11:00', price: 420, soc: 45, dispatch: 0 },
    { time: '12:00', price: 380, soc: 45, dispatch: 0 },
    { time: '13:00', price: 350, soc: 50, dispatch: 0 },
    { time: '14:00', price: 320, soc: 48, dispatch: 0 },
    { time: '15:00', price: 300, soc: 45, dispatch: 0 },
    { time: '16:00', price: 450, soc: 45, dispatch: 20 },
    { time: '17:00', price: 650, soc: 35, dispatch: 20 },
    { time: '18:00', price: 800, soc: 20, dispatch: 20 },
    { time: '19:00', price: 720, soc: 15, dispatch: 0 },
    { time: '20:00', price: 580, soc: 20, dispatch: 0 },
    { time: '21:00', price: 480, soc: 30, dispatch: 0 },
    { time: '22:00', price: 420, soc: 40, dispatch: 0 },
    { time: '23:00', price: 380, soc: 45, dispatch: 0 },
  ];

  const dispatchLog = [
    { id: 1, time: '12:00', type: 'Arb Sell', vol: 5.0, color: 'text-blue-600' },
    { id: 2, time: '11:45', type: 'FCR', vol: 0.2, color: 'text-purple-600' },
    { id: 3, time: '11:00', type: 'Idle', vol: 0.0, color: 'text-gray-600' },
    { id: 4, time: '04:00', type: 'Charge', vol: -18.0, color: 'text-green-600' },
    { id: 5, time: '03:00', type: 'Charge', vol: -20.0, color: 'text-green-600' },
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="text-sm font-medium text-gray-900 mb-2">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center justify-between space-x-4 mb-1">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }}></div>
                <span className="text-xs text-gray-600">{entry.name}</span>
              </div>
              <span className="text-xs font-medium text-gray-900">
                {entry.dataKey === 'price' ? `${entry.value} PLN` : 
                 entry.dataKey === 'soc' ? `${entry.value}%` : 
                 `${entry.value} MW`}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('assetCockpit.title')}</h1>
        <p className="text-gray-600 text-sm leading-relaxed max-w-2xl">
          {t('assetCockpit.subtitle')}
        </p>
      </div>

      {/* Asset Info Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <BatteryCharging className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{assetInfo.name}</h2>
              <div className="flex items-center space-x-4 mt-1">
                <div className="flex items-center space-x-1 text-sm text-gray-600">
                  <MapPin className="w-4 h-4" />
                  <span>{assetInfo.location}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-600">{assetInfo.status}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Metrics */}
          <div className="flex items-center space-x-8">
            <div className="text-center">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">SOC</p>
              <p className="text-2xl font-bold text-gray-900">{assetInfo.soc}<span className="text-lg">%</span></p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">POWER</p>
              <p className="text-2xl font-bold text-green-600">
                +{assetInfo.power} <span className="text-sm font-normal">MW</span>
              </p>
              <p className="text-xs text-gray-500">{assetInfo.powerStatus}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">CYCLES YTD</p>
              <p className="text-2xl font-bold text-gray-900">{assetInfo.cyclesYtd}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Dispatch & Price Chart */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              {t('assetCockpit.chart.title')}
            </h2>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-gray-800 rounded-sm"></div>
                <span className="text-sm text-gray-600">{t('assetCockpit.chart.price')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-400 rounded-sm"></div>
                <span className="text-sm text-gray-600">{t('assetCockpit.chart.soc')}</span>
              </div>
            </div>
          </div>

          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={dispatchData} margin={{ top: 20, right: 60, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis 
                  dataKey="time" 
                  stroke="#9ca3af" 
                  fontSize={12}
                  tickLine={false}
                  interval={2}
                />
                <YAxis 
                  yAxisId="left"
                  stroke="#9ca3af" 
                  fontSize={12}
                  tickLine={false}
                  domain={[-25, 100]}
                  label={{ value: 'SoC %', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: '#6b7280' } }}
                />
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  stroke="#9ca3af" 
                  fontSize={12}
                  tickLine={false}
                  domain={[0, 800]}
                  label={{ value: 'Price (PLN)', angle: 90, position: 'insideRight', style: { fontSize: 12, fill: '#6b7280' } }}
                />
                <Tooltip content={<CustomTooltip />} />
                <defs>
                  <linearGradient id="socGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#60a5fa" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#60a5fa" stopOpacity={0.05}/>
                  </linearGradient>
                </defs>
                <Area 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="soc" 
                  fill="url(#socGradient)" 
                  stroke="#60a5fa"
                  strokeWidth={2}
                  name={t('assetCockpit.chart.soc')}
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="price" 
                  stroke="#1f2937"
                  strokeWidth={2}
                  dot={false}
                  name={t('assetCockpit.chart.price')}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Dispatch Log */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t('assetCockpit.dispatchLog.title')}
          </h2>

          <div className="space-y-3">
            {dispatchLog.map((log) => (
              <div key={log.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-900">{log.time}</span>
                    <span className={`text-sm font-bold ${log.vol >= 0 ? 'text-gray-900' : 'text-gray-900'}`}>
                      {log.vol > 0 ? '+' : ''}{log.vol.toFixed(1)} MW
                    </span>
                  </div>
                  <span className={`text-xs font-medium ${log.color}`}>
                    {log.type}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AssetCockpit;

